import { propertyAddressLine } from "../helpers/utils";

export const formatAddressObject = (address) => {
  if (!address || typeof address !== "object") {
    return null;
  }

  const street = address.address || address.address_line || address.street;
  return (
    propertyAddressLine(street, address.city, address.state, address.zip) ||
    null
  );
};

export const formatSessionPropertyLabel = (
  session,
  { emptyLabel = "No property yet", completedEmptyLabel = "ID verified — no property enrolled yet" } = {},
) => {
  if (session?.display_property) {
    return session.display_property;
  }

  const fromProperty = formatAddressObject(session?.property_address);
  if (fromProperty) {
    return fromProperty;
  }

  const fromIdAddress = formatAddressObject(session?.id_address);
  if (fromIdAddress) {
    return fromIdAddress;
  }

  if (session?.status === "completed") {
    return completedEmptyLabel;
  }

  return emptyLabel;
};

export const statusBadgeClass = (status) => {
  if (status === "completed") {
    return "bg-emerald-50 text-emerald-700 border-emerald-200";
  }
  if (status === "pending") {
    return "bg-amber-50 text-amber-700 border-amber-200";
  }
  if (status === "failed" || status === "expired") {
    return "bg-red-50 text-red-700 border-red-200";
  }
  return "bg-muted text-muted-foreground border-border";
};

export const shouldShowEmailSentBadge = (session) =>
  Boolean(session?.invite_email_sent_at) && session?.status === "pending";

/** ID validation history belongs on the onboarding pipeline, not established client workspaces. */
export const shouldShowServicerWorkspaceIdValidation = (
  client,
  { propertiesCount = null } = {},
) => {
  const count = Number(
    propertiesCount ?? client?.properties_count ?? client?.property_count ?? 0,
  );
  if (!Number.isNaN(count) && count > 0) {
    return false;
  }

  const status = String(client?.status || "").toLowerCase();
  if (status === "active") {
    return false;
  }

  return true;
};

/** Onboarding CTA — hide once the client has properties or an active account. */
export const shouldShowContinueProtection = (
  client,
  { propertiesCount = null } = {},
) => {
  const { step } = getSignupLastStep(client);
  // Step >= 4: prefer Add Property / Set PIN over continue-protection CTA.
  if (step != null && step >= 4) {
    return false;
  }
  return shouldShowServicerWorkspaceIdValidation(client, { propertiesCount });
};

/** Parse signup resume marker from listing/workspace client payloads. */
export const getSignupLastStep = (client) => {
  const raw = client?.json_last_step;
  if (!raw) {
    return { step: null, stepKey: null };
  }

  let parsed = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { step: null, stepKey: null };
    }
  }

  const step = Number(parsed?.step);
  return {
    step: Number.isNaN(step) ? null : step,
    stepKey: parsed?.step_key || null,
  };
};

/**
 * A C.A.R. agent's Add Property payment for a client who has not finished
 * signup (below step 4) ends with the welcome email, as Add Client does: the
 * client sets their own PIN. Admins and other servicers keep the PIN screen.
 */
export const carPaymentEndsWithWelcomeEmail = ({
  isServiceProvider = false,
  completeProtectionOnly = false,
  client = null,
} = {}) => {
  if (!isServiceProvider || !completeProtectionOnly) {
    return false;
  }
  const { step } = getSignupLastStep(client);
  return step != null && step < 4;
};

/**
 * Interrupted signup: property exists, account still pending,
 * and signup has not completed payment (step < 4).
 * Hidden once last step is past 4 (only Add Property remains).
 */
export const shouldShowResumePayment = (
  client,
  { propertiesCount = null } = {},
) => {
  const status = String(client?.status || "").toLowerCase();
  if (status !== "pending") {
    return false;
  }

  const count = Number(
    propertiesCount ?? client?.properties_count ?? client?.property_count ?? 0,
  );
  if (Number.isNaN(count) || count <= 0) {
    return false;
  }

  const { step, stepKey } = getSignupLastStep(client);
  if (stepKey === "SEND_WELCOME_EMAIL") {
    return false;
  }
  // Step >= 4: payment resume CTA is done (step > 4 also hides other CTAs).
  if (step != null && step >= 4) {
    return false;
  }

  return true;
};

const toIdKey = (value) => {
  if (value == null || value === "") {
    return null;
  }
  return String(value);
};

/** Collect property_id keys from closing-invoices `items` for match checks. */
export const buildClosingInvoicePropertyIdSet = (invoiceItems = []) => {
  const ids = new Set();
  if (!Array.isArray(invoiceItems)) {
    return ids;
  }
  invoiceItems.forEach((item) => {
    const key = toIdKey(item?.property_id ?? item?.propertyId);
    if (key) {
      ids.add(key);
    }
  });
  return ids;
};

const workspacePropertyIdKeys = (property = {}) =>
  [property?.property_id, property?.id, property?.propertyId]
    .map(toIdKey)
    .filter(Boolean);

/**
 * Property still needs payment: last step > 4, subscription_status
 * "Not Applicable", and property_id not on any closing invoice.
 */
export const isServicerPropertyPaymentPending = (
  property,
  closingInvoicePropertyIds = new Set(),
  { lastStep = null } = {},
) => {
  const step =
    lastStep != null
      ? Number(lastStep)
      : getSignupLastStep(property?.owner?.[0] || property).step;
  if (step == null || Number.isNaN(step) || step <= 4) {
    return false;
  }

  const status = String(property?.subscription_status || "").trim();
  if (status !== "Not Applicable") {
    return false;
  }

  const keys = workspacePropertyIdKeys(property);
  if (keys.length === 0) {
    return false;
  }

  return !keys.some((key) => closingInvoicePropertyIds.has(key));
};

/**
 * Per-property Complete Payment (beside category chip):
 * Enhanced / Complete Protection only, when payment is still pending.
 */
export const shouldShowPropertyCompletePayment = (
  property,
  closingInvoicePropertyIds = new Set(),
  options = {},
) => {
  if (
    !isServicerPropertyPaymentPending(
      property,
      closingInvoicePropertyIds,
      options,
    )
  ) {
    return false;
  }

  const tier = String(
    property?.product_tier || property?.category || "",
  )
    .trim()
    .toLowerCase();
  return (
    tier === "enhanced_monitoring" || tier === "complete_protection"
  );
};

/**
 * Closing invoice / payment done, but security PIN was skipped (e.g. interrupted
 * after Create & send invoice). Show a resume CTA on the client workspace.
 * Also shown for active clients who still have no PIN (step >= 4).
 * Complete Protection only — hide for Basic / Enhanced Monitoring.
 */
export const shouldShowSetSecurityPin = (
  client,
  { propertiesCount = null, hasOpenClosingInvoice = false, property = null } = {},
) => {
  const tier = String(
    property?.product_tier ||
      property?.category ||
      client?.product_tier ||
      client?.category ||
      "",
  )
    .trim()
    .toLowerCase();
  if (tier !== "complete_protection") {
    return false;
  }

  const status = String(client?.status || "").toLowerCase();
  if (status !== "pending" && status !== "active") {
    return false;
  }

  const count = Number(
    propertiesCount ?? client?.properties_count ?? client?.property_count ?? 0,
  );
  if (Number.isNaN(count) || count <= 0) {
    return false;
  }

  if (client?.security_pin_added === true || client?.has_security_pin === true) {
    return false;
  }

  const { step } = getSignupLastStep(client);

  // Payment/invoice completed (step 4+) or an open PAC invoice exists.
  if (hasOpenClosingInvoice) {
    return true;
  }

  return step != null && step >= 4;
};

export const shouldShowResendInvite = (sessions, { loading = false } = {}) => {
  if (loading) {
    return false;
  }

  if (!Array.isArray(sessions) || sessions.length === 0) {
    return false;
  }

  return sessions.some((session) => session.status === "pending");
};
